import { getUnreadNotifications, markAllNotificationsAsRead, markNotificationAsRead } from '@/apis/notifications';
import { computed, onUnmounted, ref } from 'vue';

interface UserNotification {
  uuid: string;
  dateCreated: string;
  title: string;
  message: string;
  orderUuid?: string;
  orderStatus?: string;
  isRead: boolean;
}

const POLLING_INTERVAL = 30000; // 30 seconds

// IMPORTANT: These MUST be outside the function to be truly global/singleton
const notifications = ref<UserNotification[]>([]);
const lastCheckedCount = ref(0);
const previousNotificationUuids = ref<Set<string>>(new Set());
let pollingTimer: number | null = null;
let isFirstLoad = true; // Track if this is the very first fetch ever

// Create audio element for notification sound
const notificationSound = new Audio();
// Using a longer, more noticeable notification sound (double beep)
// This is a base64-encoded WAV file with a pleasant double-tone notification sound
notificationSound.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

// Create a better sound using Web Audio API
const createNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    return () => {
      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // First tone: 800Hz
      oscillator1.type = 'sine';
      oscillator1.frequency.value = 800;
      
      // Second tone: 1000Hz (slightly higher)
      oscillator2.type = 'sine';
      oscillator2.frequency.value = 1000;
      
      // Connect oscillators to gain node
      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Volume envelope (start loud, fade out)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01); // Quick attack
      gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3); // Fade out
      
      // Play first beep
      oscillator1.start(audioContext.currentTime);
      oscillator1.stop(audioContext.currentTime + 0.15);
      
      // Play second beep (slightly delayed)
      oscillator2.start(audioContext.currentTime + 0.15);
      oscillator2.stop(audioContext.currentTime + 0.35);
    };
  } catch (e) {
    console.warn('Web Audio API not supported, using fallback sound');
    return null;
  }
};

const playWebAudioSound = createNotificationSound();
notificationSound.volume = 0.7; // Increased volume to 70%

export function useNotifications() {
  const unreadCount = computed(() => notifications.value.filter(n => !n.isRead).length);
  const hasNewNotifications = computed(() => unreadCount.value > lastCheckedCount.value);

  const fetchNotifications = async () => {
    try {
      const { data } = await getUnreadNotifications();
      const newNotifications = data.data as UserNotification[];
      
      // Check if there are truly NEW notifications by comparing UUIDs
      const currentUuids = new Set(newNotifications.map(n => n.uuid));
      const newUuids = newNotifications.filter(n => !previousNotificationUuids.value.has(n.uuid));
      const hasNewNotifications = newUuids.length > 0;
      
      // Play sound only if there are genuinely new notifications and this isn't the first load
      if (hasNewNotifications && !isFirstLoad) {
        playNotificationSound();
      } else if (hasNewNotifications && isFirstLoad) {
        isFirstLoad = false; // Mark that we've completed first load
      }
      
      // Update state
      notifications.value = newNotifications;
      lastCheckedCount.value = newNotifications.length;
      previousNotificationUuids.value = currentUuids;
      
      return newNotifications;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return [];
    }
  };

  const playNotificationSound = () => {
    try {
      // Try to use Web Audio API first (better quality, more control)
      if (playWebAudioSound) {
        try {
          playWebAudioSound();
          return;
        } catch (err) {
          // Fallback to HTML5 Audio
        }
      }
      
      // Fallback to HTML5 Audio element
      notificationSound.currentTime = 0;
      const playPromise = notificationSound.play();
      
      if (playPromise !== undefined) {
        playPromise
          .catch(err => {
            // Browser blocked autoplay - set up fallback listeners
            const enableAudio = () => {
              if (playWebAudioSound) {
                try {
                  playWebAudioSound();
                } catch (e) {
                  // Silent fail
                }
              } else {
                notificationSound.play().catch(() => {});
              }
              document.removeEventListener('click', enableAudio);
              document.removeEventListener('keydown', enableAudio);
            };
            
            document.addEventListener('click', enableAudio, { once: true });
            document.addEventListener('keydown', enableAudio, { once: true });
          });
      }
    } catch (error) {
      // Silent fail
    }
  };

  const startPolling = () => {
    // Fetch immediately
    fetchNotifications();
    
    // Then poll every 30 seconds
    if (pollingTimer) {
      clearInterval(pollingTimer);
    }
    pollingTimer = window.setInterval(() => {
      fetchNotifications();
    }, POLLING_INTERVAL);
  };

  const stopPolling = () => {
    if (pollingTimer) {
      clearInterval(pollingTimer);
      pollingTimer = null;
    }
  };

  const markAsRead = async (notificationUuid: string) => {
    try {
      await markNotificationAsRead(notificationUuid);
      // Remove notification from the list immediately (since we only show unread)
      const index = notifications.value.findIndex(n => n.uuid === notificationUuid);
      if (index !== -1) {
        notifications.value.splice(index, 1);
      }
      lastCheckedCount.value = notifications.value.length;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      // Clear all notifications from the list (since we only show unread)
      notifications.value = [];
      lastCheckedCount.value = 0;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Cleanup on component unmount
  onUnmounted(() => {
    stopPolling();
  });

  return {
    notifications: computed(() => notifications.value),
    unreadCount,
    hasNewNotifications,
    fetchNotifications,
    startPolling,
    stopPolling,
    markAsRead,
    markAllAsRead,
  };
}

